const MEDIA_ERROR_PREFIX = '[MEDIA_LOAD_ERROR]'
const MEDIA_ERROR_STORAGE_KEY = 'one2fan.media-monitor-errors'
const MAX_REPORTED_ERRORS = 10
const ERROR_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000

const VIDEO_ERROR_NAMES = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED'
}

let isInstalled = false

function isMediaElement (element) {
  return element instanceof HTMLImageElement ||
    element instanceof HTMLVideoElement
}

function getMediaElement (element) {
  if (isMediaElement(element)) {
    return element
  }

  if (!(element instanceof HTMLSourceElement)) {
    return null
  }

  if (element.parentElement instanceof HTMLVideoElement) {
    return element.parentElement
  }

  if (element.parentElement instanceof HTMLPictureElement) {
    return element.parentElement.querySelector('img')
  }

  return null
}

function getDeclaredSource (media) {
  if (media instanceof HTMLImageElement) {
    return media.currentSrc ||
      media.getAttribute('src') ||
      media.getAttribute('srcset') ||
      ''
  }

  const source = media.querySelector('source[src]')

  return media.getAttribute('src') ||
    (source && source.getAttribute('src')) ||
    media.currentSrc ||
    ''
}

function isInternalSource (source) {
  return source.indexOf('blob:') === 0 || source.indexOf('data:') === 0
}

function sanitizeUrl (source) {
  if (!source) {
    return 'N/A'
  }

  if (source.indexOf('blob:') === 0) {
    return 'blob:'
  }

  if (source.indexOf('data:') === 0) {
    return 'data:'
  }

  try {
    const url = new URL(source, window.location.href)

    url.search = ''
    url.hash = ''

    return url.href
  }
  catch (error) {
    return source.split('?')[0].split('#')[0]
  }
}

function getVideoErrorName (video) {
  if (!video.error) {
    return 'RESOURCE_ERROR'
  }

  return VIDEO_ERROR_NAMES[video.error.code] || 'MEDIA_ERR_UNKNOWN'
}

function createErrorReportState () {
  return {
    expiresAt: Date.now() + ERROR_LIMIT_WINDOW_MS,
    fingerprints: []
  }
}

function loadErrorReportState () {
  const fallbackState = createErrorReportState()

  try {
    const storedValue = window.localStorage.getItem(MEDIA_ERROR_STORAGE_KEY)

    if (!storedValue) {
      return fallbackState
    }

    const storedState = JSON.parse(storedValue)
    const isValidState = typeof storedState.expiresAt === 'number' &&
      storedState.expiresAt > Date.now() &&
      Array.isArray(storedState.fingerprints)

    if (!isValidState) {
      return fallbackState
    }

    return {
      expiresAt: storedState.expiresAt,
      fingerprints: storedState.fingerprints
        .filter(function (fingerprint) {
          return typeof fingerprint === 'string'
        })
        .slice(0, MAX_REPORTED_ERRORS)
    }
  }
  catch {
    return fallbackState
  }
}

function saveErrorReportState (state) {
  try {
    window.localStorage.setItem(
      MEDIA_ERROR_STORAGE_KEY,
      JSON.stringify(state)
    )
  }
  catch {
    // The in-memory limit still works when localStorage is unavailable.
  }
}

export default {
  install: () => {
    if (isInstalled) {
      return
    }

    isInstalled = true

    const knownSources = new WeakMap()
    let errorReportState = loadErrorReportState()

    function rememberSource (media, source) {
      const mediaSource = source || getDeclaredSource(media)

      if (mediaSource && !isInternalSource(mediaSource)) {
        knownSources.set(media, mediaSource)
      }
    }

    function getMediaSource (media) {
      const declaredSource = getDeclaredSource(media)

      if (declaredSource && !isInternalSource(declaredSource)) {
        return declaredSource
      }

      return knownSources.get(media) || declaredSource
    }

    function reportMediaError (media) {
      if (media.dataset.mediaMonitor === 'ignore') {
        return
      }

      if (!window.logger || typeof window.logger.error !== 'function') {
        return
      }

      rememberSource(media)

      const isVideo = media instanceof HTMLVideoElement
      const mediaType = isVideo ? 'video' : 'image'
      const reason = isVideo ? getVideoErrorName(media) : 'RESOURCE_ERROR'
      const source = getMediaSource(media)

      if (!source) {
        return
      }

      if (errorReportState.expiresAt <= Date.now()) {
        errorReportState = createErrorReportState()
      }

      const sanitizedUrl = sanitizeUrl(source)
      const assetId = media.dataset.assetId || 'N/A'
      const mediaIdentifier = assetId !== 'N/A'
        ? 'asset:' + assetId
        : 'url:' + sanitizedUrl
      const fingerprint = mediaType + '|' + mediaIdentifier

      if (
        errorReportState.fingerprints.includes(fingerprint) ||
        errorReportState.fingerprints.length >= MAX_REPORTED_ERRORS
      ) {
        return
      }

      errorReportState.fingerprints.push(fingerprint)
      saveErrorReportState(errorReportState)

      const payload = {
        mediaType: mediaType,
        reason: reason,
        url: sanitizedUrl,
        assetId: assetId,
        page: window.location.pathname,
        online: navigator.onLine
      }

      if (isVideo) {
        payload.errorCode = media.error ? media.error.code : 'N/A'
        payload.errorMessage = media.error && media.error.message
          ? media.error.message
          : 'N/A'
        payload.networkState = media.networkState
        payload.readyState = media.readyState
      }

      window.logger.error(
        MEDIA_ERROR_PREFIX +
        '[' + mediaType.toUpperCase() + ']' +
        '[' + reason + '] ' +
        JSON.stringify(payload)
      )
    }

    function inspectMedia (media) {
      rememberSource(media)

      if (media instanceof HTMLImageElement) {
        const hasSource = Boolean(
          media.getAttribute('src') ||
          media.getAttribute('srcset') ||
          media.closest('picture')?.querySelector('source[srcset]')
        )

        if (hasSource && media.complete && media.naturalWidth === 0) {
          reportMediaError(media)
        }

        return
      }

      if (media.error) {
        reportMediaError(media)
      }
    }

    function inspectTree (node) {
      if (!(node instanceof Element)) {
        return
      }

      const media = getMediaElement(node)

      if (media) {
        inspectMedia(media)
      }

      node.querySelectorAll('img, video').forEach(inspectMedia)
    }

    function handleMediaError (event) {
      const eventTarget = event.target
      const media = getMediaElement(eventTarget)

      if (!media) {
        return
      }

      if (eventTarget instanceof HTMLSourceElement) {
        window.setTimeout(function () {
          const hasNoSource = media instanceof HTMLVideoElement &&
            media.networkState === HTMLMediaElement.NETWORK_NO_SOURCE

          if (media.error || hasNoSource) {
            reportMediaError(media)
          }
        }, 0)

        return
      }

      reportMediaError(media)
    }

    function handleMediaLoadStart (event) {
      const media = getMediaElement(event.target)

      if (media) {
        rememberSource(media)
      }
    }

    window.addEventListener('error', handleMediaError, true)
    window.addEventListener('loadstart', handleMediaLoadStart, true)

    const mutationObserver = new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.type === 'attributes') {
          const media = getMediaElement(record.target)

          if (media) {
            rememberSource(media, record.oldValue)
            rememberSource(media)
          }

          return
        }

        record.addedNodes.forEach(inspectTree)
      })
    })

    mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['src', 'srcset'],
      attributeOldValue: true,
      childList: true,
      subtree: true
    })

    document.querySelectorAll('img, video').forEach(inspectMedia)
  }
}
